import {
  IconDeviceFloppy,
  IconEdit,
  IconRestore,
  IconTrash,
  IconX,
} from '@u21/tabler-icons';
import { U21SplitButton } from 'app/shared/u21-ui/components';
import styled, { css } from 'styled-components';

import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { Dispatch, SetStateAction, useState } from 'react';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { useUpdateSavedFilter } from 'app/modules/filters/queries/useUpdateSavedFilter';
import { alpha } from '@mui/system/colorManipulator';

interface Props {
  defaultFilters: Filter[];
  dirty: boolean;
  filterContext: FilterContext;
  filters: Filter[];
  onAppliedSavedFilterChange: Dispatch<SetStateAction<SavedFilter | undefined>>;
  onFiltersChange: (filters: Filter[]) => void;
  savedFilter: SavedFilter;
}

export const AppliedSavedFilterButton = ({
  defaultFilters,
  dirty,
  filterContext,
  filters,
  onAppliedSavedFilterChange,
  onFiltersChange,
  savedFilter,
}: Props) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const {
    mutateAsync: updateSavedFilter,
    isPending: isUpdateSavedFilterPending,
  } = useUpdateSavedFilter();

  return (
    <>
      <StyledU21SplitButton
        $dirty={dirty}
        disableTitleCase
        loading={isUpdateSavedFilterPending}
        tooltip={dirty ? `Unsaved changes to ${savedFilter.title}` : undefined}
        variant="ghost"
        items={[
          ...(dirty
            ? [
                {
                  icon: <IconDeviceFloppy />,
                  text: 'Update filters',
                  onClick: async () => {
                    try {
                      await updateSavedFilter({ id: savedFilter.id, filters });
                    } catch {}
                  },
                },
                {
                  icon: <IconRestore />,
                  text: 'Discard changes',
                  onClick: () => onFiltersChange(savedFilter.filters),
                },
              ]
            : []),
          {
            icon: <IconEdit />,
            text: 'Edit',
            onClick: () => setEditOpen(true),
          },
          {
            color: 'error' as const,
            icon: <IconTrash />,
            text: 'Delete',
            onClick: () => setDeleteOpen(true),
          },
          {
            icon: <IconX />,
            text: 'Clear filter',
            onClick: () => {
              onAppliedSavedFilterChange(undefined);
              onFiltersChange(defaultFilters);
            },
          },
        ]}
      >
        {savedFilter.title}
      </StyledU21SplitButton>
      <EditSavedFilterModal
        filters={filters}
        onClose={() => setEditOpen(false)}
        onUpdated={onAppliedSavedFilterChange}
        open={editOpen}
        savedFilter={savedFilter}
      />
      <DeleteSavedFilterModal
        filterContext={filterContext}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => onAppliedSavedFilterChange(undefined)}
        open={deleteOpen}
        savedFilter={savedFilter}
      />
    </>
  );
};

const StyledU21SplitButton = styled(U21SplitButton)<{ $dirty: boolean }>`
  border-radius: 8px 8px 0 0;
  border-bottom: 2px solid
    ${(props) =>
      props.$dirty
        ? props.theme.palette.warning.main
        : props.theme.palette.primary.main};

  background-color: ${(props) => alpha(props.theme.palette.primary.main, 0.08)};

  & .MuiToggleButtonGroup-firstButton {
    // mimics U21Tabs padding
    padding: 0 12px;
    border-bottom-left-radius: 0;
    color: ${(props) =>
      props.$dirty
        ? props.theme.palette.warning.main
        : props.theme.palette.primary.main};
    ${(props) =>
      props.$dirty &&
      css`
        font-weight: 600;
      `}
  }

  & .MuiToggleButtonGroup-lastButton {
    border-bottom-right-radius: 0;
    color: ${(props) =>
      props.$dirty
        ? props.theme.palette.warning.main
        : props.theme.palette.primary.main};
    border-left-color: ${(props) =>
      alpha(
        props.$dirty
          ? props.theme.palette.warning.main
          : props.theme.palette.primary.main,
        0.48,
      )};
  }
`;
